import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  setExtractedData, 
  setOcrInputID, 
  setTaricJsonErrors, 
  clearTaricJsonErrors,
  fetchCreditStats,
  filterDeclarationsByErrors,
  setSuccessfulExpedientes,
  setErrorExpedientes
} from '../store/reducers';
import { apiFetch, refreshAccessToken } from '../utils/api';
import toast from 'react-hot-toast';

// Helper function to play success sound (two-tone chime)
const playSuccessSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine';
        
        gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play two-tone chime (success sound)
    playTone(523.25, 0.2, 0); // C5
    playTone(659.25, 0.3, 150); // E5
  } catch (error) {
    console.error('Error playing success sound:', error);
  }
};

// Helper function to play error sound (softer, gentler tone)
const playErrorSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    const playTone = (frequency, duration, delay = 0) => {
      setTimeout(() => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        
        oscillator.frequency.value = frequency;
        oscillator.type = 'sine'; // Softer sound instead of harsh sawtooth
        
        // Lower volume - start at 0.15 instead of 0.3
        gainNode.gain.setValueAtTime(0.15, audioContext.currentTime);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);
        
        oscillator.start(audioContext.currentTime);
        oscillator.stop(audioContext.currentTime + duration);
      }, delay);
    };
    
    // Play softer error sound (gentle descending tones)
    playTone(349.23, 0.2, 0); // F4 (softer than G4)
    playTone(293.66, 0.25, 150); // D4 (softer than D#4)
  } catch (error) {
    console.error('Error playing error sound:', error);
  }
};

export const useDocumentProcessing = () => {
  const dispatch = useDispatch();
  // Get declarations from Redux to access expediente values
  const declarations = useSelector((state) => state.preparedJson?.declarations || []);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isGeneratingTaric, setIsGeneratingTaric] = useState(false);
  const [hasGenerationError, setHasGenerationError] = useState(false);
  const [generationErrorMessage, setGenerationErrorMessage] = useState("");
  const [processingCancelled, setProcessingCancelled] = useState(false);

  const processDocuments = async (documents, onStatusUpdate, specifyAmount = [], userContext = '') => {
    setProcessingCancelled(false);
    // Clear all flags when starting new document processing to ensure fresh start
    sessionStorage.removeItem('originalExtractedData');
    sessionStorage.removeItem('documentReadyReceived');
    sessionStorage.removeItem('hasCompletedJob');
    sessionStorage.removeItem('lastJobId'); // Clear lastJobId when starting a new job

    try {
      const formData = new FormData();

      if (documents && documents.length > 0) {
        documents.forEach(file => {
          formData.append('files', file);
        });
      }

      if (specifyAmount.length > 0) {
        formData.append('specifyAmount', JSON.stringify(specifyAmount));
      }

      if (userContext && typeof userContext === 'string' && userContext.trim().length > 0) {
        formData.append('user_context', userContext.trim());
      }

      const url = `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/ocr_json_input/generate`;

      // Use apiFetch for automatic token refresh on 401 errors
      let response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json'
          // Don't set Content-Type - let browser set it with boundary for FormData
        },
        body: formData
      });

      // Parse response first to check for "already have a job" message
      let responseData;
      try {
        responseData = await response.json();
      } catch (e) {
        // If response is not JSON and not OK, throw error
        if (!response.ok) {
          throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
        }
        throw new Error('Invalid response from server');
      }

      // Handle "already have a job in the queue" case - check if job actually exists
      if ((!response.ok && (responseData.message?.includes('already have a job') || responseData.error?.includes('already have a job'))) ||
          (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue'))) {
        // Silently handle "already have a job" case - this is expected behavior, not an error
        // Note: Browser will still show 400 in console, but we handle it gracefully
        
        try {
          const currentJobResponse = await apiFetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/current`, {
            headers: {
              'accept': 'application/json'
            }
          });
          
          // If no current job exists (404) or it returns an error, allow new job creation
          if (!currentJobResponse.ok || currentJobResponse.status === 404) {
            console.log('No current job found in queue');
            // The API incorrectly reported a job exists, but there isn't one
            throw new Error('The system reported an existing job, but none was found. This may be a temporary issue. Please try again in a moment.');
          }
          
          const currentJobData = await currentJobResponse.json();
          
          // If current job endpoint returns error or no job, allow new job
          if (currentJobData.status === 'error' || !currentJobData.id) {
            console.log('No valid current job found');
            throw new Error('The system reported an existing job, but none was found. This may be a temporary issue. Please try again in a moment.');
          }
          
          const jobId = currentJobData.id;
          
          // Notify about the jobId so streaming can start - stream will handle all status updates
          if (jobId) {
                  onStatusUpdate({
              jobId: jobId,
              processingStatus: 'processing',
              currentStep: 1,
              completedSteps: [0]
            });
          }
          
          return responseData;
        } catch (checkError) {
          // If checking current job failed or no job exists, throw the original error message
          console.error('Error checking current job or no job exists:', checkError);
          throw checkError;
        }
      }

      // Check HTTP status code for other errors (not "already have a job")
      if (!response.ok) {
        let errorMessage = `HTTP error! status: ${response.status}`;
        errorMessage = responseData.message || responseData.error || responseData.detail || errorMessage;
        
        // Handle token expiration explicitly
        if (response.status === 401 || 
            errorMessage?.toLowerCase().includes('token expired') || 
            errorMessage?.toLowerCase().includes('expired')) {
          console.log('[Document Processing] Token expired, attempting refresh...');
          const refreshData = await refreshAccessToken();
          
          if (refreshData && refreshData.access_token) {
            console.log('[Document Processing] Token refreshed, retrying request...');
            // Retry the request with the new token
            response = await apiFetch(url, {
              method: 'POST',
              headers: {
                'accept': 'application/json'
              },
              body: formData
            }, false); // Don't retry again if this fails
            
            // Re-parse response after retry
            try {
              responseData = await response.json();
            } catch (e) {
              if (!response.ok) {
                throw new Error(response.statusText || `HTTP error! status: ${response.status}`);
              }
              throw new Error('Invalid response from server');
            }
            
            // If retry still fails, throw the error
            if (!response.ok) {
              errorMessage = responseData.message || responseData.error || responseData.detail || `HTTP error! status: ${response.status}`;
              throw new Error(errorMessage);
            }
          } else {
            // Token refresh failed
            throw new Error('Token expired. Please log in again.');
          }
        } else {
          // Only log actual errors, not handled cases
          if (!responseData.message?.includes('already have a job') && !responseData.error?.includes('already have a job')) {
            console.error('Document processing request failed:', errorMessage);
          }
          throw new Error(errorMessage);
        }
      }

      // Check if the response indicates an error (not just "already have a job")
      if (responseData.status === 'error' && !responseData.message?.includes('already have a job in the queue')) {
        console.error('Document processing error:', responseData.message || responseData.error);
        throw new Error(responseData.message || responseData.error || 'Document processing failed');
      }

      // If we have a jobId, notify stream to connect - stream will handle all status updates and data
      if (responseData.jobId ?? (responseData.status === 'error' && responseData.message?.includes('already have a job in the queue'))) {
        let jobId = responseData.jobId;
        if (!jobId && responseData.status === 'error') {
          console.log('No jobId found, fetching current job...');
          const currentJobResponse = await apiFetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/queue/current`, {
            headers: {
              'accept': 'application/json'
            }
          });
          
          if (!currentJobResponse.ok || currentJobResponse.status === 404) {
            throw new Error('No active job found. Please try again.');
          }
          
          const currentJobData = await currentJobResponse.json();
          
          if (currentJobData.status === 'error' || !currentJobData.id) {
            throw new Error('No active job found. Please try again.');
          }
          
          jobId = currentJobData.id;
        }
        
        // Immediately notify about the jobId so streaming can start
        // The stream will handle all status updates, data extraction, and completion
        if (jobId) {
          onStatusUpdate({
            jobId: jobId,
            processingStatus: 'processing',
            currentStep: 1,
            completedSteps: [0]
          });
        }
      }

      return responseData;
    } catch (error) {
      if (!error.response) {
        throw error;
      }
      return error.response;
    }
  };

  const generateTaricDeclaration = async (preparedJson) => {
    try {
      setIsGeneratingTaric(true);
      setHasGenerationError(false);
      setGenerationErrorMessage("");
      dispatch(clearTaricJsonErrors());
      
      // Debug: Check environment variable
      const apiKey = process.env.REACT_APP_DOCS_API_KEY;
      if (!apiKey) {
        throw new Error('API configuration error: Missing API key');
      }
      
      // Try with trailing slash (consistent with GET requests)
      const url = `https://${apiKey}/api/items/taric_json_output/`;
      
      const response = await apiFetch(url, {
        method: 'POST',
        headers: {
          'accept': 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(preparedJson.taricJson)
      });

      const responseData = await response.json();

      // Check for errors first
      const hasErrors = responseData.errors && Array.isArray(responseData.errors) && responseData.errors.length > 0;
      const hasSuccess = responseData.success && Array.isArray(responseData.success) && responseData.success.length > 0;

      // Handle errors
      if (hasErrors) {
        // Set error state so modal shows error
        setHasGenerationError(true);
        setGenerationErrorMessage("Some declarations have validation errors. Please check the details.");
        
        // Play error sound
        playErrorSound();

        // Dispatch errors to Redux
        dispatch(setTaricJsonErrors(responseData));
        
        // Extract error expedientes and store in Redux
        const errorExpedientes = responseData.errors.map(item => 
          item.expediente || `Export #${(item.exportacionesIndex || 0) + 1}`
        );
        dispatch(setErrorExpedientes(errorExpedientes));
        
        // Extract exportacionesIndex values from errors for filtering
        const errorIndices = responseData.errors
            .map(err => err.exportacionesIndex)
            .filter(idx => idx !== undefined && typeof idx === 'number' && idx >= 0);
          
          if (errorIndices.length > 0) {
            dispatch(filterDeclarationsByErrors(errorIndices));
          }
      } else {
        // Clear error state if no errors
        setHasGenerationError(false);
        setGenerationErrorMessage("");
        dispatch(setErrorExpedientes([]));
      }

      // Handle success - only show success toasts if there are successful items
      if (hasSuccess) {
        // Play success sound
        playSuccessSound();
        
        // Extract successful expedientes and store in Redux
        const successfulExpedientes = responseData.success.map(item => 
          item.expediente || `Export #${(item.exportacionesIndex || 0) + 1}`
        );
        dispatch(setSuccessfulExpedientes(successfulExpedientes));
        
      } else {
        dispatch(setSuccessfulExpedientes([]));
      }

      if (!response.ok) {
        throw new Error(responseData.message || 'Failed to process Taric declaration');
      }

      // Return success based on whether there are errors
      const isSuccessful = !hasErrors;
      return { success: isSuccessful, data: responseData };
    } catch (error) {
      setHasGenerationError(true);
      setGenerationErrorMessage(error.message || "An error occurred while generating the Taric declaration.");
      
      // Play error sound for catch errors
      playErrorSound();
      
      return { success: false, error };
    } finally {
      setIsGeneratingTaric(false);
    }
  };

  return {
    isProcessing,
    setIsProcessing,
    isGeneratingTaric,
    hasGenerationError,
    generationErrorMessage,
    processingCancelled,
    setProcessingCancelled,
    processDocuments,
    generateTaricDeclaration
  };
}; 